import { useState } from "react";
import { Sparkles, X, ChevronDown, ChevronUp, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Persistent, dismissible callout shown at the top of the Budget Builder after
 * an AI takeoff pre-fills the budget. Renders a summary line + bulleted notes
 * so the user can quickly spot-check what the AI did.
 *
 * Notes can be:
 *   - plain strings "TAG: text" (legacy)
 *   - objects { tag, text, target } where target is { kind:"field"|"scope", ... }
 *
 * When a note has a target, the row acts like a link that jumps to the right
 * spot (parent supplies onNavigateTarget).
 *
 * Each row has a resolved checkbox — parent owns the resolved state via
 * `resolved` (Set<number>) + `onToggleResolved(index)`. MISSING notes with a
 * `field` target auto-resolve when the user fills that input; everything else
 * is manual.
 */
const TAG_STYLES = {
  MATCHED:          { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
  ASSUMPTION:       { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-700"   },
  "LOW CONFIDENCE": { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-700"   },
  MISC:             { bg: "bg-slate-100",  border: "border-slate-200",   text: "text-slate-600"   },
  MISSING:          { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-700"   },
  WARNING:          { bg: "bg-red-50",     border: "border-red-200",     text: "text-red-700"     },
};

function normalize(raw) {
  if (typeof raw === "string") {
    const m = raw.match(/^([A-Z][A-Z ]{1,20}):\s*(.*)$/);
    if (m && TAG_STYLES[m[1].trim()]) return { tag: m[1].trim(), text: m[2], target: null };
    return { tag: "", text: raw, target: null };
  }
  return { tag: raw?.tag || "", text: raw?.text || "", target: raw?.target || null };
}

export default function AITakeoffNotes({ meta, onDismiss, onNavigateTarget, resolved, onToggleResolved }) {
  const [open, setOpen] = useState(true);
  if (!meta) return null;
  const raw = Array.isArray(meta.notes) ? meta.notes : (meta.notes ? [meta.notes] : []);
  const notes = raw.map(normalize).filter(n => n.text);
  const isResolved = (i) => !!(resolved && resolved.has && resolved.has(i));
  const resolvedCount = notes.reduce((acc, _, i) => acc + (isResolved(i) ? 1 : 0), 0);

  const summary = [
    `${meta.scopes_extracted || 0} scope${(meta.scopes_extracted || 0) === 1 ? "" : "s"}`,
    `${meta.line_items_extracted || 0} line item${(meta.line_items_extracted || 0) === 1 ? "" : "s"}`,
  ];
  if (meta.line_items_matched != null) summary.push(`${meta.line_items_matched} matched`);
  if (meta.line_items_misc) summary.push(`${meta.line_items_misc} misc`);
  if (notes.length > 0) summary.push(`${resolvedCount}/${notes.length} resolved`);

  return (
    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200" data-testid="ai-takeoff-notes">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <div className="mt-0.5 w-6 h-6 rounded-full bg-[#161616] text-white flex items-center justify-center shrink-0">
            <Sparkles className="w-3.5 h-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-[#161616]">AI Takeoff — review before saving</span>
              {meta.filename && (
                <span className="text-[11px] text-slate-500 truncate" data-testid="ai-notes-filename">from {meta.filename}</span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5" data-testid="ai-notes-summary">{summary.join(" · ")}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {notes.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpen(o => !o)}
              className="h-7 px-2 text-xs text-slate-600 hover:bg-slate-100 rounded-sm"
              data-testid="ai-notes-toggle"
            >
              {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {open ? "Hide notes" : `Show ${notes.length} note${notes.length === 1 ? "" : "s"}`}
            </Button>
          )}
          <button
            onClick={onDismiss}
            className="p-1 text-slate-400 hover:text-slate-700 rounded-sm"
            aria-label="Dismiss AI notes"
            title="Dismiss"
            data-testid="ai-notes-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {open && notes.length > 0 && (
        <ul className="mt-3 ml-8 space-y-1.5" data-testid="ai-notes-list">
          {notes.map((n, i) => {
            const style = n.tag ? TAG_STYLES[n.tag] : null;
            const hasTarget = !!(n.target && onNavigateTarget);
            const done = isResolved(i);

            const checkbox = (
              <button
                type="button"
                role="checkbox"
                aria-checked={done}
                onClick={(e) => { e.stopPropagation(); onToggleResolved && onToggleResolved(i); }}
                className={`shrink-0 mt-0.5 w-4 h-4 rounded-sm border flex items-center justify-center transition-colors ${
                  done
                    ? "bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600"
                    : "bg-white border-slate-300 hover:border-emerald-400"
                }`}
                title={done ? "Mark as unresolved" : "Mark as resolved"}
                data-testid={`ai-note-${i}-checkbox`}
              >
                {done && <Check className="w-3 h-3" strokeWidth={3} />}
              </button>
            );

            const body = (
              <div className={`flex items-start gap-2 min-w-0 flex-1 ${done ? "opacity-60" : ""}`}>
                {n.tag ? (
                  <span className={`shrink-0 px-1.5 py-0.5 rounded-sm border ${style ? `${style.bg} ${style.border} ${style.text}` : "bg-slate-100 border-slate-200 text-slate-600"} text-[10px] font-semibold`}>
                    {n.tag}
                  </span>
                ) : (
                  <span className="shrink-0 text-slate-400 mt-0.5">•</span>
                )}
                <span className={`text-slate-700 ${done ? "line-through text-slate-500" : ""}`}>{n.text}</span>
                {hasTarget && !done && (
                  <ArrowRight className="w-3 h-3 text-slate-400 group-hover:text-[#161616] shrink-0 self-center transition-transform group-hover:translate-x-0.5" />
                )}
              </div>
            );

            return (
              <li key={i} data-testid={`ai-note-${i}`} className="flex items-start gap-2">
                {checkbox}
                {hasTarget ? (
                  <button
                    type="button"
                    onClick={() => onNavigateTarget(n.target)}
                    className="group flex-1 text-left flex items-start gap-2 text-xs leading-relaxed px-1 py-0.5 -mx-1 rounded-sm hover:bg-slate-100 transition-colors cursor-pointer"
                    data-testid={`ai-note-${i}-btn`}
                    title="Jump to the relevant area"
                  >
                    {body}
                  </button>
                ) : (
                  <div className="flex-1 flex items-start gap-2 text-xs leading-relaxed px-1 py-0.5 -mx-1">
                    {body}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {open && notes.length === 0 && (
        <div className="mt-2 ml-8 text-[11px] text-slate-400 italic">No specific observations — everything mapped cleanly.</div>
      )}
    </div>
  );
}
